import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import {
  Zap, Menu, X, ArrowRight, ShieldCheck, Cpu, Cloud, Sparkles, Activity,
  Battery, Radar, Landmark, Building2, Store, Wrench, LineChart, Lock,
  KeyRound, Server, Database, Layers, CheckCircle2, Wifi, ChevronRight,
} from "lucide-react";

const NAV = [
  { id: "platform", label: "Platform" },
  { id: "roadmap", label: "Roadmap" },
  { id: "partners", label: "Partners" },
  { id: "security", label: "Security" },
  { id: "architecture", label: "Architecture" },
];

export default function Landing() {
  const { user } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const jump = (id) => {
    setMobileOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden">
      {/* Ambient glow layers */}
      <div className="fixed inset-0 -z-10 pointer-events-none"
           style={{ backgroundImage: "radial-gradient(circle at 15% 10%, hsl(217 91% 60% / 0.15), transparent 40%), radial-gradient(circle at 90% 20%, hsl(160 90% 50% / 0.10), transparent 40%), radial-gradient(circle at 50% 90%, hsl(280 90% 60% / 0.08), transparent 40%)" }} />
      <div className="fixed inset-0 -z-10 pointer-events-none opacity-[0.15]"
           style={{ backgroundImage: "linear-gradient(hsl(var(--border)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--border)) 1px, transparent 1px)", backgroundSize: "56px 56px" }} />

      {/* Nav */}
      <header className={`fixed top-0 inset-x-0 z-40 transition-all ${scrolled ? "glass-header" : "bg-transparent"}`}>
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center gap-4">
          <Link to="/" className="flex items-center gap-2" data-testid="landing-logo">
            <div className="w-8 h-8 rounded-lg bg-blue-600 grid place-items-center">
              <Zap className="w-5 h-5 text-white" strokeWidth={2.5} />
            </div>
            <div className="font-heading font-semibold tracking-tight">RushGro</div>
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground ml-1">Technology</span>
          </Link>
          <nav className="hidden md:flex items-center gap-1 ml-6 flex-1">
            {NAV.map((n) => (
              <button key={n.id} onClick={() => jump(n.id)}
                      className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground rounded-md transition-colors"
                      data-testid={`landing-nav-${n.id}`}>{n.label}</button>
            ))}
          </nav>
          <div className="hidden md:flex items-center gap-2 ml-auto">
            {user ? (
              <Button onClick={() => nav("/dashboard")} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="landing-open-dashboard">
                Open Dashboard <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <>
                <Link to="/login" data-testid="landing-signin-btn"><Button variant="ghost">Sign in</Button></Link>
                <Link to="/signup" data-testid="landing-signup-btn">
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white">Sign up <ArrowRight className="w-4 h-4 ml-1" /></Button>
                </Link>
              </>
            )}
          </div>
          <button className="md:hidden ml-auto" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Menu">
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
        {mobileOpen && (
          <div className="md:hidden border-t bg-card/95 backdrop-blur">
            <div className="px-6 py-4 flex flex-col gap-2">
              {NAV.map((n) => <button key={n.id} onClick={() => jump(n.id)} className="text-left py-2 text-sm">{n.label}</button>)}
              <div className="pt-3 flex gap-2 border-t">
                <Link to="/login" className="flex-1"><Button variant="outline" className="w-full">Sign in</Button></Link>
                <Link to="/signup" className="flex-1"><Button className="w-full bg-blue-600 text-white">Sign up</Button></Link>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Hero */}
      <section className="pt-32 md:pt-40 pb-16 md:pb-24">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-xs uppercase tracking-widest text-blue-500 font-medium">Energy Infrastructure · v2026</div>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-heading font-semibold tracking-tight leading-[1.05]">
              Technology powering the <span className="rushgro-gradient-text">future of energy infrastructure.</span>
            </h1>
            <p className="mt-5 text-muted-foreground max-w-xl">
              RushGro combines Energy-as-a-Service, live IoT telemetry, AI-driven analytics, predictive maintenance and enterprise-grade software into a single operating system for India's smartest commercial energy network.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/signup" data-testid="hero-signup-btn">
                <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white">
                  Become a Customer <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Button variant="outline" size="lg" onClick={() => jump("partners")} data-testid="hero-partner-btn">
                Become a Leasing Partner
              </Button>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
              {[
                { k: "110 kW", v: "Assets deployed" },
                { k: "99.7%", v: "Uptime target" },
                { k: "4,821", v: "IoT nodes" },
              ].map((s) => (
                <div key={s.v} className="border rounded-lg p-3 bg-card/60">
                  <div className="font-heading text-xl font-semibold">{s.k}</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">{s.v}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Live RushIoT preview panel */}
          <Card className="relative rounded-2xl p-5 border bg-card/70 backdrop-blur">
            <div className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-2 text-emerald-500"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> Live · RushIoT</div>
              <div className="text-muted-foreground">Node cluster · IN-BLR</div>
            </div>
            <div className="mt-5 grid grid-cols-2 gap-4">
              {[
                { icon: Activity, label: "Energy Flow", value: "+12.4%", accent: "text-blue-500 bg-blue-500/10" },
                { icon: Battery, label: "Asset health", value: "98.2 / 100", accent: "text-emerald-500 bg-emerald-500/10" },
                { icon: Wifi, label: "IoT nodes", value: "4,821", accent: "text-fuchsia-500 bg-fuchsia-500/10" },
                { icon: Cloud, label: "Cloud sync", value: "Live", accent: "text-amber-500 bg-amber-500/10" },
                { icon: Zap, label: "Cycles", value: "1.2M", accent: "text-blue-500 bg-blue-500/10" },
                { icon: ShieldCheck, label: "Alerts", value: "0 open", accent: "text-emerald-500 bg-emerald-500/10" },
              ].map((m) => (
                <div key={m.label} className="rounded-xl border p-3">
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground">
                    <span className={`w-6 h-6 rounded grid place-items-center ${m.accent}`}><m.icon className="w-3.5 h-3.5" /></span>
                    {m.label}
                  </div>
                  <div className="font-heading text-lg font-semibold mt-1">{m.value}</div>
                </div>
              ))}
            </div>
            {/* animated bars */}
            <div className="mt-5 flex items-end gap-1 h-16">
              {Array.from({ length: 32 }).map((_, i) => (
                <div key={i} className="flex-1 rounded-sm bg-gradient-to-t from-blue-600/40 to-blue-400"
                     style={{ height: `${20 + Math.abs(Math.sin(i / 3) * 70)}%` }} />
              ))}
            </div>
            <div className="mt-3 text-[11px] text-muted-foreground">Aggregate power draw · last 32 minutes</div>
          </Card>
        </div>
      </section>

      {/* Platform ecosystem */}
      <section id="platform" className="py-16 md:py-24 border-t">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-xs uppercase tracking-widest text-blue-500">Our Technology Ecosystem</div>
          <h2 className="mt-3 text-3xl md:text-4xl font-heading font-semibold tracking-tight max-w-2xl">Four platforms. One intelligent energy network.</h2>
          <p className="mt-3 text-muted-foreground max-w-2xl">From hardware telemetry to boardroom reports — every layer is engineered in-house.</p>
          <div className="mt-10 grid md:grid-cols-2 gap-4">
            {[
              { icon: Cpu, name: "RushOS", tag: "Coming Soon", desc: "Smart operating system for energy infrastructure.",
                items: ["Asset monitoring", "Outlet management", "Remote diagnostics", "Smart alerts", "Energy analytics"] },
              { icon: Sparkles, name: "RushVision AI", tag: "Coming Soon", desc: "AI-powered monitoring engine.",
                items: ["Predictive asset failure", "AI health scoring", "Usage optimisation", "Intelligent charging", "Demand forecasting"] },
              { icon: Cloud, name: "RushIoT Cloud", tag: "In Development", desc: "Cloud platform connecting every deployed asset.",
                items: ["Live asset status", "Temperature & voltage", "Charging cycles", "GPS tracking", "OTA firmware updates"] },
              { icon: LineChart, name: "RushAnalytics", tag: "In Development", desc: "Enterprise dashboard for brands and leasing partners.",
                items: ["Asset performance", "Power usage trends", "ROI reports", "Monthly lease reports", "Predictive insights"] },
            ].map((p) => (
              <Card key={p.name} className="p-6 rounded-2xl border bg-card/70 hover:border-blue-500/60 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/10 text-blue-500 grid place-items-center">
                    <p.icon className="w-5 h-5" />
                  </div>
                  <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border border-amber-500/30 text-amber-500 bg-amber-500/5">{p.tag}</span>
                </div>
                <div className="mt-4 font-heading text-2xl font-semibold">{p.name}</div>
                <div className="mt-1 text-sm text-muted-foreground">{p.desc}</div>
                <ul className="mt-4 grid grid-cols-2 gap-y-1.5 text-xs">
                  {p.items.map((i) => <li key={i} className="flex items-center gap-1.5 text-muted-foreground"><ChevronRight className="w-3 h-3 text-blue-500" />{i}</li>)}
                </ul>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Roadmap */}
      <section id="roadmap" className="py-16 md:py-24 border-t">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-xs uppercase tracking-widest text-blue-500">Coming soon</div>
          <h2 className="mt-3 text-3xl md:text-4xl font-heading font-semibold tracking-tight max-w-2xl">A roadmap that turns energy into intelligence.</h2>
          <p className="mt-3 text-muted-foreground max-w-2xl">Every quarter, we ship deeper analytics, tighter automation and richer partner tools.</p>
          <div className="mt-10 grid md:grid-cols-3 gap-4">
            {[
              { q: "Q3 · 2026", items: ["AI Asset Health Engine", "Smart Charging Optimisation", "Digital Twin Monitoring"] },
              { q: "Q4 · 2026", items: ["RushOS Enterprise Dashboard", "Customer Mobile App", "Leasing Partner Portal 2.0"] },
              { q: "Q1 · 2027", items: ["Carbon Savings Dashboard", "Energy Marketplace", "Asset Swapping Intelligence", "AI Maintenance Assistant"] },
            ].map((r) => (
              <Card key={r.q} className="p-5 rounded-2xl border">
                <div className="text-xs uppercase tracking-widest text-blue-500">{r.q}</div>
                <ul className="mt-4 space-y-2 text-sm">
                  {r.items.map((i) => <li key={i} className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500" />{i}</li>)}
                </ul>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Leasing partners */}
      <section id="partners" className="py-16 md:py-24 border-t">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-xs uppercase tracking-widest text-blue-500">Leasing Partner Technology</div>
            <h2 className="mt-3 text-3xl md:text-4xl font-heading font-semibold tracking-tight">Total transparency for every rupee invested.</h2>
            <p className="mt-3 text-muted-foreground max-w-lg">Live locations, live health, live income. Everything a partner needs — in one dashboard.</p>
            <ul className="mt-6 space-y-3 text-sm">
              {["Portfolio, ROI and uptime tracked to the minute",
                "Buyback timelines, insurance status and maintenance history",
                "Real-time deployment map across every outlet",
                "Automated monthly lease income statements",
              ].map((i) => (
                <li key={i} className="flex gap-3"><CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5" /> <span>{i}</span></li>
              ))}
            </ul>
            <div className="mt-6">
              <Link to="/signup"><Button className="bg-blue-600 hover:bg-blue-700 text-white">Request Partner Access <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
            </div>
          </div>
          <Card className="rounded-2xl border p-5 bg-card/70 backdrop-blur">
            <div className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-2 text-blue-500"><Landmark className="w-3.5 h-3.5" /> Partner OS · Live</div>
              <div className="text-muted-foreground">Portfolio · A+</div>
            </div>
            <div className="mt-5">
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Monthly Lease Income</div>
              <div className="mt-1 font-heading text-3xl font-semibold">₹1.86 Cr <span className="text-emerald-500 text-sm font-medium">+12.4%</span></div>
              <div className="text-xs text-muted-foreground">FY 26 · YTD</div>
            </div>
            <div className="mt-6">
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Deployment Status · 1,284 assets</div>
              <div className="mt-2 h-2 rounded-full overflow-hidden bg-muted flex">
                <div className="bg-emerald-500" style={{ width: "86%" }} />
                <div className="bg-blue-500" style={{ width: "9%" }} />
                <div className="bg-amber-500" style={{ width: "3%" }} />
                <div className="bg-fuchsia-500" style={{ width: "2%" }} />
              </div>
              <div className="mt-2 flex flex-wrap gap-3 text-[11px] text-muted-foreground">
                <span><span className="inline-block w-2 h-2 rounded-full bg-emerald-500 mr-1" />Deployed 86%</span>
                <span><span className="inline-block w-2 h-2 rounded-full bg-blue-500 mr-1" />In transit 9%</span>
                <span><span className="inline-block w-2 h-2 rounded-full bg-amber-500 mr-1" />Maintenance 3%</span>
                <span><span className="inline-block w-2 h-2 rounded-full bg-fuchsia-500 mr-1" />Reserved 2%</span>
              </div>
            </div>
            <div className="mt-6 grid grid-cols-2 gap-3 text-sm">
              {[["Battery health", "97.8%", "+0.3%"], ["Uptime", "99.72%", "SLA ✓"], ["ROI (12m)", "17.4%", "+1.1%"], ["Maintenance", "0.4/mo", "-12%"]].map(([k, v, d]) => (
                <div key={k} className="border rounded-lg p-3">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{k}</div>
                  <div className="mt-1 font-heading text-lg">{v}</div>
                  <div className="text-[11px] text-emerald-500">{d}</div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </section>

      {/* Customer tools */}
      <section className="py-16 md:py-24 border-t">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-xs uppercase tracking-widest text-blue-500">Customer Technology</div>
          <h2 className="mt-3 text-3xl md:text-4xl font-heading font-semibold tracking-tight max-w-2xl">Operational tools built for enterprise brands.</h2>
          <p className="mt-3 text-muted-foreground max-w-2xl">From live telemetry to invoices — a single control room for every outlet.</p>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {[
              { icon: Radar, t: "Live asset status" }, { icon: Store, t: "Outlet monitoring" },
              { icon: Activity, t: "Energy consumption" }, { icon: Battery, t: "Backup history" },
              { icon: Wrench, t: "Maintenance tickets" }, { icon: Sparkles, t: "Service requests" },
              { icon: Building2, t: "Installation tracking" }, { icon: ShieldCheck, t: "Warranty management" },
              { icon: LineChart, t: "Invoice downloads" }, { icon: KeyRound, t: "Subscription management" },
            ].map((f) => (
              <div key={f.t} className="border rounded-xl p-4 bg-card/60 hover:border-blue-500/60 transition-colors">
                <f.icon className="w-5 h-5 text-blue-500" />
                <div className="mt-3 text-sm font-medium">{f.t}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Data flow */}
      <section className="py-16 md:py-24 border-t">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-xs uppercase tracking-widest text-blue-500">Data Intelligence Platform</div>
          <h2 className="mt-3 text-3xl md:text-4xl font-heading font-semibold tracking-tight max-w-2xl">From cell to boardroom in milliseconds.</h2>
          <p className="mt-3 text-muted-foreground max-w-2xl">Data flows through six layers of the RushGro stack — each adding intelligence to the next.</p>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {[
              { t: "Battery", s: "Cell + BMS", icon: Battery },
              { t: "IoT device", s: "Edge telemetry", icon: Cpu },
              { t: "RushIoT Cloud", s: "Live ingestion", icon: Cloud },
              { t: "AI Analytics", s: "Prediction + scoring", icon: Sparkles },
              { t: "Business Dashboard", s: "Ops + Finance", icon: LineChart },
              { t: "Insights", s: "Decisions + alerts", icon: Radar },
            ].map((n, i, arr) => (
              <div key={n.t} className="relative border rounded-xl p-4 bg-card/60">
                <div className="w-9 h-9 rounded-lg bg-blue-500/10 text-blue-500 grid place-items-center"><n.icon className="w-4 h-4" /></div>
                <div className="mt-3 font-medium text-sm">{n.t}</div>
                <div className="text-[11px] text-muted-foreground">{n.s}</div>
                {i < arr.length - 1 && (
                  <ChevronRight className="hidden lg:block absolute -right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-500/60" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security */}
      <section id="security" className="py-16 md:py-24 border-t">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-xs uppercase tracking-widest text-blue-500">Security & Data Management</div>
          <h2 className="mt-3 text-3xl md:text-4xl font-heading font-semibold tracking-tight max-w-2xl">Enterprise-grade by default. Not by upgrade.</h2>
          <p className="mt-3 text-muted-foreground max-w-2xl">Every layer of RushGro is engineered around trust, resilience and provable compliance.</p>
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: Lock, t: "End-to-end encryption", d: "TLS 1.3 in transit, AES-256 at rest across every data plane." },
              { icon: KeyRound, t: "Role-based access", d: "Granular RBAC for partners, ops, service and finance roles." },
              { icon: Server, t: "Secure cloud infra", d: "Isolated VPCs, private subnets and hardened container images." },
              { icon: Database, t: "Daily automated backups", d: "Point-in-time recovery windows across all critical stores." },
              { icon: Radar, t: "Immutable audit logs", d: "Every partner and admin action logged for compliance." },
              { icon: ShieldCheck, t: "Privacy compliance", d: "Aligned with DPDP Act and ISO 27001 controls." },
            ].map((s) => (
              <Card key={s.t} className="rounded-2xl border p-5">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/10 text-emerald-500 grid place-items-center"><s.icon className="w-5 h-5" /></div>
                <div className="mt-4 font-heading text-lg font-semibold">{s.t}</div>
                <div className="mt-1 text-sm text-muted-foreground">{s.d}</div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Architecture */}
      <section id="architecture" className="py-16 md:py-24 border-t">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-xs uppercase tracking-widest text-blue-500">Technical Architecture</div>
          <h2 className="mt-3 text-3xl md:text-4xl font-heading font-semibold tracking-tight max-w-2xl">Seven layers. One operating system for energy.</h2>
          <p className="mt-3 text-muted-foreground max-w-2xl">A vertically integrated stack that scales from a single outlet to a national network.</p>
          <div className="mt-10 space-y-2">
            {[
              ["L1", "Customer Portal", "Live status · service · billing"],
              ["L2", "Leasing Partner Portal", "Portfolio · ROI · uptime"],
              ["L3", "RushOS Platform", "Orchestration · workflows"],
              ["L4", "AI Analytics Engine", "Predict · optimise · score"],
              ["L5", "Connected Asset Network", "Asset mesh · telemetry"],
              ["L6", "Smart Asset Hardware", "Cells · safety · sensors"],
              ["L7", "Energy Data Fabric", "Ingest · store · secure"],
            ].map(([id, name, desc]) => (
              <div key={id} className="border rounded-xl p-4 flex items-center gap-4 bg-card/60">
                <div className="w-10 h-10 rounded-lg bg-blue-600 text-white grid place-items-center font-heading font-semibold">{id}</div>
                <div className="flex-1">
                  <div className="font-medium">{name}</div>
                  <div className="text-xs text-muted-foreground">{desc}</div>
                </div>
                <Layers className="w-4 h-4 text-muted-foreground" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 md:py-28 border-t">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-5xl font-heading font-semibold tracking-tight leading-[1.1]">
            Join India's next-generation <span className="rushgro-gradient-text">energy infrastructure platform.</span>
          </h2>
          <p className="mt-4 text-muted-foreground max-w-2xl mx-auto">
            Whether you're a brand deploying power at scale, a partner backing the future of leasing, or an operator looking for a modern service layer — RushGro is your stack.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link to="/signup"><Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="cta-signup-btn">Become a Customer <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
            <Button variant="outline" size="lg" onClick={() => jump("partners")}>Partner With Us</Button>
            <Link to="/login"><Button variant="ghost" size="lg">Sign in</Button></Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      {/* Footer */}
<footer className="border-t py-10 bg-muted/20">
  <div className="max-w-7xl mx-auto px-6">
    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-8">

      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded bg-blue-600 grid place-items-center">
          <Zap className="w-4 h-4 text-white" strokeWidth={2.5} />
        </div>

        <div>
          <div className="font-semibold text-foreground">
            RushGro Technology
          </div>
          <div className="text-xs text-muted-foreground">
            © {new Date().getFullYear()} RushGro · Energy-as-a-Service
          </div>
        </div>
      </div>

      <div>
        <p className="text-sm font-semibold text-foreground mb-3">
          Legal & Policies
        </p>

        <div className="flex flex-wrap gap-3">
          <Link
            to="/privacy-policy"
            className="px-4 py-2 rounded-md border border-blue-500/50 text-sm font-medium text-blue-500 hover:bg-blue-500 hover:text-white transition-colors"
          >
            Privacy Policy
          </Link>

          <Link
            to="/terms-and-conditions"
            className="px-4 py-2 rounded-md border border-blue-500/50 text-sm font-medium text-blue-500 hover:bg-blue-500 hover:text-white transition-colors"
          >
            Terms & Conditions
          </Link>

          <Link
            to="/refund-policy"
            className="px-4 py-2 rounded-md border border-blue-500/50 text-sm font-medium text-blue-500 hover:bg-blue-500 hover:text-white transition-colors"
          >
            Refund & Cancellation Policy
          </Link>
        </div>
      </div>

      <div className="flex gap-4 text-sm">
        <Link to="/login" className="hover:text-blue-500">
          Sign in
        </Link>

        <Link to="/signup" className="hover:text-blue-500">
          Sign up
        </Link>

        <button
          onClick={() => jump("security")}
          className="hover:text-blue-500"
        >
          Security
        </button>
      </div>

    </div>
  </div>
</footer>
      {/* <footer className="border-t py-10">
        <div className="max-w-7xl mx-auto px-6 flex flex-wrap items-center gap-4 justify-between text-xs text-muted-foreground">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded bg-blue-600 grid place-items-center"><Zap className="w-3.5 h-3.5 text-white" strokeWidth={2.5} /></div>
            <span>© {new Date().getFullYear()} RushGro Technology · Energy-as-a-Service</span>
          </div>
          
          <div className="flex flex-wrap gap-4">
  <Link to="/privacy-policy" className="hover:text-foreground">
    Privacy Policy
  </Link>

  <Link to="/terms-and-conditions" className="hover:text-foreground">
    Terms & Conditions
  </Link>

  <Link to="/refund-policy" className="hover:text-foreground">
    Refund Policy
  </Link>

  <button
    onClick={() => jump("security")}
    className="hover:text-foreground"
  >
    Security
  </button>

  <Link to="/login" className="hover:text-foreground">
    Sign in
  </Link>
</div>
        </div>
      </footer> */}
    </div>
  );
}
